import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface TrackingConfig {
  google_ads_id: string;
  google_ads_conversion_label: string;
  google_analytics_id: string;
  callrail_account_id: string;
  callrail_swap_target: string;
  facebook_pixel_id: string;
  microsoft_ads_id: string;
  default_phone_number: string;
}

// Global cache for tracking config
let cachedConfig: TrackingConfig | null = null;
// Guard so we only inject DOM scripts once per page load.
let scriptsInjected = false;

async function fetchTrackingConfig(): Promise<TrackingConfig> {
  if (cachedConfig) return cachedConfig;

  const { data, error } = await supabase
    .from("tracking_config")
    .select("key, value, enabled");

  if (error) {
    if (import.meta.env.DEV) console.error("Failed to fetch tracking config:", error);
    return getDefaultConfig();
  }

  const config: TrackingConfig = getDefaultConfig();
  data?.forEach((row: { key: string; value: string | null; enabled: boolean }) => {
    if (row.enabled && row.value && row.key in config) {
      const key = row.key as keyof TrackingConfig;
      config[key] = row.value;
    }
  });

  cachedConfig = config;
  return config;
}

function getDefaultConfig(): TrackingConfig {
  return {
    google_ads_id: "",
    google_ads_conversion_label: "",
    google_analytics_id: "",
    callrail_account_id: "",
    callrail_swap_target: "",
    facebook_pixel_id: "",
    microsoft_ads_id: "",
    default_phone_number: "(214) 646-3802",
  };
}

// Clear cache (useful when config is updated)
export function clearTrackingConfigCache() {
  cachedConfig = null;
}

// Hard-validate every tracking ID before interpolating it into an inline
// script. A compromised admin account could otherwise store a payload that
// breaks out of the JS string context and executes as stored XSS.
function safe(v: string, re: RegExp): string {
  return re.test(v) ? v : "";
}

function injectScriptOnce(props: { src?: string; async?: boolean; id?: string; text?: string }) {
  if (typeof document === "undefined") return;
  if (props.id && document.getElementById(props.id)) return;
  if (props.src && document.querySelector(`script[src="${props.src}"]`)) return;

  const script = document.createElement("script");
  if (props.id) script.id = props.id;
  if (props.async) script.async = true;
  if (props.src) script.src = props.src;
  if (props.text) script.textContent = props.text;
  document.head.appendChild(script);
}

function injectTrackingScripts(config: TrackingConfig) {
  if (typeof window === "undefined" || scriptsInjected) return;
  scriptsInjected = true;

  const google_ads_id = safe(config.google_ads_id, /^AW-[A-Za-z0-9_-]{1,40}$/);
  const google_analytics_id = safe(
    config.google_analytics_id,
    /^(G|UA|GT)-[A-Za-z0-9_-]{1,40}$/,
  );
  const facebook_pixel_id = safe(config.facebook_pixel_id, /^\d{6,20}$/);
  const microsoft_ads_id = safe(config.microsoft_ads_id, /^[A-Za-z0-9]{4,20}$/);

  // Google Tag (gtag.js) - Google Ads & Analytics
  if (google_ads_id || google_analytics_id) {
    const id = google_ads_id || google_analytics_id;
    injectScriptOnce({
      src: `https://www.googletagmanager.com/gtag/js?id=${id}`,
      async: true,
      id: `gtag-js-${id}`,
    });
    injectScriptOnce({
      id: "gtag-inline",
      text: [
        "window.dataLayer = window.dataLayer || [];",
        "function gtag(){dataLayer.push(arguments);}",
        "gtag('js', new Date());",
        google_analytics_id ? `gtag('config', '${google_analytics_id}');` : "",
        google_ads_id ? `gtag('config', '${google_ads_id}');` : "",
      ].join("\n"),
    });
  }

  // Facebook Pixel
  if (facebook_pixel_id) {
    injectScriptOnce({
      id: "fb-pixel",
      text: [
        "!function(f,b,e,v,n,t,s)",
        "{if(f.fbq)return;n=f.fbq=function(){n.callMethod?",
        "n.callMethod.apply(n,arguments):n.queue.push(arguments)};",
        "if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';",
        "n.queue=[];t=b.createElement(e);t.async=!0;",
        "t.src=v;s=b.getElementsByTagName(e)[0];",
        "s.parentNode.insertBefore(t,s)}(window, document,'script',",
        "'https://connect.facebook.net/en_US/fbevents.js');",
        `fbq('init', '${facebook_pixel_id}');`,
        "fbq('track', 'PageView');",
      ].join("\n"),
    });
  }

  // Microsoft/Bing UET Tag
  if (microsoft_ads_id) {
    injectScriptOnce({
      id: "bing-uet",
      text: [
        "(function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:'" + microsoft_ads_id + "'};",
        "o.q=w[u],w[u]=new UET(o),w[u].push('pageLoad')},n=d.createElement(t),n.src=r,n.async=1,",
        "n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!==\"loaded\"&&s!==\"complete\"||(f(),n.onload=n.onreadystatechange=null)},",
        'i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");',
      ].join("\n"),
    });
  }
}

const TrackingScripts = () => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Defer non-critical tracking until the browser is idle so it never
    // competes with hydration, LCP image, or first-input responsiveness.
    const run = () => {
      fetchTrackingConfig().then((cfg) => {
        setLoaded(true);
        injectTrackingScripts(cfg);
        if (import.meta.env.DEV) {
          console.log("Tracking scripts injected from database", cfg);
        }
      });
    };
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (typeof w.requestIdleCallback === "function") {
      const id = w.requestIdleCallback(run, { timeout: 3000 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(run, 1500);
    return () => window.clearTimeout(t);
  }, []);

  return loaded ? null : null;
};

export default TrackingScripts;

// Helper function to fire Google Ads conversion
export async function fireGoogleAdsConversion(value?: number, currency = "USD") {
  const config = await fetchTrackingConfig();

  if (!config.google_ads_id || !config.google_ads_conversion_label || typeof window === "undefined") return;

  const w = window as unknown as { gtag?: (...args: unknown[]) => void };
  const sendConversion = () => {
    const payload: Record<string, unknown> = {
      send_to: `${config.google_ads_id}/${config.google_ads_conversion_label}`,
      currency: currency,
    };
    if (value !== undefined) payload.value = value;
    w.gtag?.("event", "conversion", payload);
    if (import.meta.env.DEV) console.log("Google Ads conversion fired");
  };

  if (w.gtag) {
    sendConversion();
    return;
  }

  // Poll briefly for gtag in case the Google tag script loads after this call.
  let attempts = 0;
  const interval = window.setInterval(() => {
    attempts++;
    if (w.gtag) {
      sendConversion();
      window.clearInterval(interval);
    } else if (attempts > 30) {
      window.clearInterval(interval);
    }
  }, 100);
}

// Helper function to fire Facebook Lead event
export async function fireFacebookLead() {
  const config = await fetchTrackingConfig();

  if (config.facebook_pixel_id && typeof window !== "undefined" && (window as unknown as { fbq?: (...args: unknown[]) => void }).fbq) {
    (window as unknown as { fbq: (...args: unknown[]) => void }).fbq("track", "Lead");
    if (import.meta.env.DEV) console.log("Facebook Lead event fired");
  }
}

// Fire all conversion events (call on form submit)
export async function fireAllConversions(value?: number) {
  await Promise.all([
    fireGoogleAdsConversion(value),
    fireFacebookLead(),
  ]);
}
